"use client";

import { MoveRight } from "lucide-react";
import { usePathname } from "next/navigation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useUser } from "@clerk/nextjs";
import {
  getPublicLocaleFromPathname,
  localizePublicHref,
} from "@/lib/i18n/public-routes";

type CallToActionProps = {
  badgeText?: string;
  title?: string;
  subtitle?: string;
  primaryHref?: string;
  primaryLabel?: string;
};

export const CallToAction = ({
  badgeText,
  title,
  subtitle,
  primaryHref,
  primaryLabel,
}: CallToActionProps = {}) => {
  const { t } = useTranslation();
  const { isSignedIn } = useUser();
  const pathname = usePathname() ?? "/";
  const publicLocale = getPublicLocaleFromPathname(pathname);

  const containerVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { staggerChildren: 0.12, duration: 0.65, ease: [0.165, 0.84, 0.44, 1] },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  const localizedPrimaryHref = localizePublicHref(
    publicLocale,
    primaryHref ?? (isSignedIn ? "/dashboard" : "/sign-up"),
  );

  return (
    <motion.div
      className="relative w-full"
      initial="hidden"
      whileInView="visible"
      variants={containerVariants}
      viewport={{ once: true }}
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-x-12 top-1/3 h-[28rem] rounded-full bg-[radial-gradient(circle,_rgba(59,130,246,0.2),_rgba(255,255,255,0))] blur-3xl opacity-80 dark:bg-[radial-gradient(circle,_rgba(129,140,248,0.25),_rgba(15,23,42,0))]" />
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          className="relative overflow-hidden rounded-[3rem] border border-white/50 bg-white/80 px-6 py-12 text-center shadow-[0_40px_140px_-70px_rgba(15,23,42,0.65)] backdrop-blur-xl dark:border-white/10 dark:bg-white/10 lg:px-16 lg:py-16"
          variants={itemVariants}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 right-12 h-56 w-56 rounded-full bg-[radial-gradient(circle,_rgba(236,72,153,0.22),_rgba(255,255,255,0))] blur-3xl opacity-90 dark:bg-[radial-gradient(circle,_rgba(244,114,182,0.35),_rgba(15,23,42,0))]"
          />
          <motion.div variants={itemVariants} className="flex justify-center">
            <Badge className="rounded-full border border-white/50 bg-white/70 px-5 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-slate-600 backdrop-blur dark:border-white/10 dark:bg-white/10 dark:text-slate-200">
              {badgeText ?? t("CALLTOACTION_BADGE_TEXT")}
            </Badge>
          </motion.div>
          <motion.div className="mt-8 flex flex-col gap-4" variants={itemVariants}>
            <h2 className="bg-gradient-to-r from-slate-900 via-indigo-600 to-slate-900 bg-clip-text py-6 text-4xl font-semibold uppercase tracking-tight text-transparent md:text-5xl dark:from-white dark:via-sky-200 dark:to-white">
              {title ?? t("CALLTOACTION_TITLE_TEXT")}
            </h2>
            <p className="mx-auto max-w-xl text-lg leading-8 text-slate-600 md:text-xl dark:text-slate-300">
              {subtitle ?? t("CALLTOACTION_SUBTITLE_TEXT")}
            </p>
          </motion.div>
          <motion.div
            className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
            variants={itemVariants}
          >
            <Button
              asChild
              className="w-full rounded-full bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400 px-8 py-3 text-base font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:opacity-90 sm:w-auto"
            >
              <Link href={localizedPrimaryHref}>
                {(primaryLabel ?? t("HOME_HERO_CTA_TEXT"))} <MoveRight className="ml-3 h-4 w-4" />
              </Link>
            </Button>
            {/* <Button
              asChild
              variant="outline"
              className="w-full rounded-full border border-slate-200/70 bg-white/70 px-8 py-3 text-base font-semibold text-slate-700 backdrop-blur transition hover:border-slate-300 hover:bg-white/90 dark:border-white/10 dark:bg-slate-900/40 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-900 sm:w-auto"
            >
              <Link href="/builder">{t("HOME_CTA_STARTFREE_BUTTON")}</Link>
            </Button> */}
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};
